import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { viewTransition } from '@theme/utilities';

const CARD_SELECTOR = 'li.product-grid__item--impronte-variant-card[data-variant-id]';
const SECTION_ID = 'impronte-variant-card-fragments';
const REGISTERED_ATTR = 'data-impronte-variant-card-registered';
const HYDRATING_ATTR = 'data-impronte-variant-card-hydrating';
const HYDRATED_ATTR = 'data-impronte-variant-card-hydrated';
const FAILED_ATTR = 'data-impronte-variant-card-failed';

const fragmentCache = new Map();
const parser = new DOMParser();
let pendingFilterUpdate = false;
let scheduledFilterRehydrate = 0;

const intersectionObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(handleIntersect, {
        rootMargin: '300px 0px',
      })
    : null;

const mutationObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;

      registerVariantCards(node);
      if (pendingFilterUpdate) scheduleFilterRehydrate();
    }
  }
});

function handleIntersect(entries) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;

    const item = entry.target;
    intersectionObserver?.unobserve(item);
    hydrateVariantCard(item);
  }
}

function registerVariantCards(root = document) {
  const cards = [];

  if (root instanceof Element && root.matches(CARD_SELECTOR)) {
    cards.push(root);
  }

  if (root && typeof root.querySelectorAll === 'function') {
    cards.push(...root.querySelectorAll(CARD_SELECTOR));
  }

  for (const card of cards) {
    if (!(card instanceof HTMLElement) || card.hasAttribute(REGISTERED_ATTR)) continue;

    card.setAttribute(REGISTERED_ATTR, 'true');
    primeCardLinks(card);
    primeCardTitle(card);

    if (intersectionObserver) {
      intersectionObserver.observe(card);
    } else {
      hydrateVariantCard(card);
    }
  }
}

function scheduleFilterRehydrate() {
  if (scheduledFilterRehydrate) return;

  scheduledFilterRehydrate = requestAnimationFrame(async () => {
    scheduledFilterRehydrate = 0;

    try {
      if (viewTransition.current) {
        await viewTransition.current;
      }
    } catch {
      // Ignore aborted transitions and continue rehydration on the current DOM state.
    }

    pendingFilterUpdate = false;

    const cards = document.querySelectorAll(CARD_SELECTOR);
    for (const card of cards) {
      if (!(card instanceof HTMLElement) || card.hasAttribute(HYDRATING_ATTR)) continue;

      card.removeAttribute(HYDRATED_ATTR);
      card.removeAttribute(FAILED_ATTR);

      primeCardLinks(card);
      hydrateVariantCard(card);
    }
  });
}

function primeCardLinks(item) {
  if (!(item instanceof HTMLElement)) return;

  const productCard = item.querySelector('product-card');
  if (!(productCard instanceof HTMLElement)) return;

  const variantUrl = buildVariantUrl(item);
  if (!(variantUrl instanceof URL)) return;

  updateLinks(productCard, variantUrl.toString(), '');
}

function getCardTitle(item, fragmentsRoot = null) {
  if (!(item instanceof HTMLElement)) return '';

  return (
    item.dataset.impronteGroupTitle ||
    item.dataset.impronteVariantTitle ||
    fragmentsRoot?.dataset.impronteProductTitle ||
    ''
  );
}

function syncCardTitle(item, productCard, title) {
  if (!(item instanceof HTMLElement) || !(productCard instanceof HTMLElement) || !title) return;

  const productTitleLink = productCard.querySelector('[ref="productTitleLink"]');
  if (productTitleLink instanceof HTMLElement) {
    updateTitle(productTitleLink, title);
  }

  const productCardLabel = productCard.querySelector('.visually-hidden');
  if (productCardLabel instanceof HTMLElement) {
    productCardLabel.textContent = title;
  }

  const zoomOutTitle = item.querySelector('.product-grid-view-zoom-out--details h3');
  if (zoomOutTitle instanceof HTMLElement) {
    zoomOutTitle.textContent = title;
  }
}

function primeCardTitle(item) {
  if (!(item instanceof HTMLElement)) return;

  const productCard = item.querySelector('product-card');
  if (!(productCard instanceof HTMLElement)) return;

  const title = getCardTitle(item);
  syncCardTitle(item, productCard, title);
}

function buildVariantUrl(item) {
  const variantId = item.dataset.variantId;
  const productCardLink = item.querySelector('[ref="productCardLink"]');

  if (!(productCardLink instanceof HTMLAnchorElement) || !variantId) return null;

  const url = new URL(productCardLink.href, window.location.origin);
  url.searchParams.set('variant', variantId);

  return url;
}

async function fetchFragments(url) {
  const cacheKey = url.toString();

  if (!fragmentCache.has(cacheKey)) {
    fragmentCache.set(
      cacheKey,
      fetch(url.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      }).then(async (response) => {
        if (!response.ok) {
          throw new Error(`Variant card fragments request failed with status ${response.status}`);
        }

        return response.text();
      })
    );
  }

  const html = await fragmentCache.get(cacheKey);
  return parser.parseFromString(html, 'text/html');
}

function updateTitle(link, title) {
  if (!(link instanceof HTMLElement) || !title) return;

  const textTarget =
    link.querySelector('.title-text') ||
    link.querySelector('.text-block > *') ||
    link.querySelector('p, h1, h2, h3, h4, h5, h6, span');

  if (textTarget instanceof HTMLElement) {
    textTarget.textContent = title;
  } else {
    link.textContent = title;
  }
}

function updatePrice(productCard, fragmentsRoot) {
  const newPriceContainer = fragmentsRoot.querySelector('[data-impronte-fragment="price"] [ref="priceContainer"]');
  if (!(newPriceContainer instanceof HTMLElement)) return;

  const currentPriceContainers = productCard.querySelectorAll("product-price [ref='priceContainer']");
  for (const priceContainer of currentPriceContainers) {
    morph(priceContainer, newPriceContainer.cloneNode(true));
  }
}

function updateGallery(item, productCard, fragmentsRoot, href, title) {
  const currentGallery = productCard.querySelector('[ref="cardGallery"]');
  const nextGallery = fragmentsRoot.querySelector('[data-impronte-fragment="gallery"] [ref="cardGallery"]');

  if (!(currentGallery instanceof HTMLElement) || !(nextGallery instanceof HTMLElement)) return;

  currentGallery.innerHTML = nextGallery.innerHTML;

  const cardGalleryLink = currentGallery.querySelector('[ref="cardGalleryLink"]');
  if (cardGalleryLink instanceof HTMLAnchorElement) {
    cardGalleryLink.href = href;
    cardGalleryLink.setAttribute('aria-label', title);
  }

  const placeholderTitle = currentGallery.querySelector('.title-text');
  if (placeholderTitle instanceof HTMLElement) {
    placeholderTitle.textContent = title;
  }

  const zoomOutTitle = item.querySelector('.product-grid-view-zoom-out--details h3');
  if (zoomOutTitle instanceof HTMLElement) {
    zoomOutTitle.textContent = title;
  }
}

function syncSelectedMedia(productCard, selectedMediaId) {
  if (!(productCard instanceof HTMLElement) || !selectedMediaId) return;

  const slideshow = productCard.querySelector('[ref="cardGallery"] slideshow-component');
  if (!(slideshow instanceof HTMLElement)) return;

  const slides = slideshow.querySelectorAll('slideshow-slide[slide-id]');
  if (!slides.length) return;

  let selectedIndex = -1;
  let selectedSlide = null;

  slides.forEach((slide, index) => {
    const isSelected = slide.getAttribute('slide-id') === selectedMediaId;

    if (slide.hasAttribute('variant-image')) {
      slide.hidden = !isSelected;
    }

    if (isSelected) {
      selectedIndex = index;
      selectedSlide = slide;
    }
  });

  if (!(selectedSlide instanceof HTMLElement)) return;

  selectedSlide.hidden = false;

  slides.forEach((slide) => {
    slide.setAttribute('aria-hidden', `${slide !== selectedSlide}`);
  });

  slideshow.setAttribute('initial-slide', `${selectedIndex}`);

  if (typeof slideshow.select === 'function') {
    slideshow.select({ id: selectedMediaId }, undefined, { animate: false });
  }
}

function updateLinks(productCard, href, featuredMediaUrl) {
  const productCardLink = productCard.querySelector('[ref="productCardLink"]');
  const productTitleLink = productCard.querySelector('[ref="productTitleLink"]');
  const cardGalleryLink = productCard.querySelector('[ref="cardGalleryLink"]');

  if (productCardLink instanceof HTMLAnchorElement) {
    productCardLink.href = href;
  }

  if (productTitleLink instanceof HTMLAnchorElement) {
    productTitleLink.href = href;
  }

  if (cardGalleryLink instanceof HTMLAnchorElement) {
    cardGalleryLink.href = href;
  }

  const productCardWrapper = productCard.closest('product-card-link');
  if (productCardWrapper instanceof HTMLElement && featuredMediaUrl) {
    productCardWrapper.setAttribute('data-featured-media-url', featuredMediaUrl);
  }
}

async function hydrateVariantCard(item) {
  if (!(item instanceof HTMLElement)) return;
  if (item.hasAttribute(HYDRATED_ATTR) || item.hasAttribute(HYDRATING_ATTR)) return;

  const productCard = item.querySelector('product-card');
  if (!(productCard instanceof HTMLElement)) return;

  const variantUrl = buildVariantUrl(item);
  if (!(variantUrl instanceof URL)) return;

  const canonicalHref = `${variantUrl.pathname}${variantUrl.search}`;
  updateLinks(productCard, canonicalHref, '');

  const requestUrl = new URL(variantUrl.toString());
  requestUrl.searchParams.set('section_id', SECTION_ID);

  item.setAttribute(HYDRATING_ATTR, 'true');

  try {
    const documentFragment = await fetchFragments(requestUrl);
    const fragmentsRoot = documentFragment.querySelector('[data-impronte-variant-card-fragments]');

    if (!(fragmentsRoot instanceof HTMLElement)) {
      throw new Error('Impronte variant fragments root not found');
    }

    const href = canonicalHref;
    const title = getCardTitle(item, fragmentsRoot);
    const featuredMediaUrl = fragmentsRoot.dataset.impronteFeaturedMediaUrl || '';
    const selectedMediaId = fragmentsRoot.dataset.impronteSelectedMediaId || '';

    updateLinks(productCard, href, featuredMediaUrl);
    updatePrice(productCard, fragmentsRoot);
    updateGallery(item, productCard, fragmentsRoot, href, title);
    syncSelectedMedia(productCard, selectedMediaId);
    syncCardTitle(item, productCard, title);

    if (typeof productCard.updatedCallback === 'function') {
      productCard.updatedCallback();
    }

    item.setAttribute(HYDRATED_ATTR, 'true');
    item.removeAttribute(FAILED_ATTR);
  } catch (error) {
    console.error('Impronte variant card hydration failed', error);
    item.setAttribute(FAILED_ATTR, 'true');
  } finally {
    item.removeAttribute(HYDRATING_ATTR);
  }
}

registerVariantCards();
mutationObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
});
document.addEventListener(ThemeEvents.FilterUpdate, () => {
  pendingFilterUpdate = true;
});
